import { useCallback, useEffect, useMemo, useState } from 'react';
import { createBrowserInspector } from '@statelyai/inspect';
import { useQueryClient } from '@tanstack/react-query';
import { useFeatureFlags } from '@tn/foundation/entities/feature-flags/hooks/useFeatureFlags';
import { useRegionalConfiguration } from '@tn/foundation/entities/regional-configuration/hooks/useRegionalConfiguration';
import useRegion from '@tn/foundation/hooks/providers/useRegion';
import useTrackingProvider from '@tn/foundation/hooks/providers/useTrackingProvider';
import useTranslation from '@tn/foundation/hooks/providers/useTranslation';
import useRegionRoute from '@tn/foundation/hooks/routes/useRegionRoute';
import { useMachine } from '@xstate/react';
import { useRouter } from 'next/router';
import { UnknownMachineConfig } from 'xstate';

import { flowRoutes } from '../consts';
import { useStateMachineContext } from '../context';
import { composeStateMachine } from '../entities/composeHelpers';
import { DTC_EVENTS } from '../entities/dtc/events';
import { FeatureFlags } from '../graphql/constants';
import { FlowId } from '../types';

import { useInspectorKeyboardShortcut } from './useInspectorKeyboardShortcut';

const inspector = createBrowserInspector({ autoStart: false });

// Confluence documentation for FlowBuilder
// https://paramount.atlassian.net/wiki/spaces/teno/pages/593397311/Flow+Builder+Component
const useFlowMachine = (machineConfig: UnknownMachineConfig) => {
    const { region } = useRegion();
    const { getConfigByKey } = useRegionalConfiguration({ appName: 'signup', region });
    const router = useRouter();
    const queryClient = useQueryClient();
    const trackingProvider = useTrackingProvider();
    const trackEvent = trackingProvider?.trackEvent;
    const { t } = useTranslation();

    // This logic is needed to correctly update the machine when the config actually changes, using the comparison in the useEffect below
    const [currentMachineConfig, setCurrentMachineConfig] = useState(machineConfig);

    // Enable inspector keyboard shortcut in non-production environments
    useInspectorKeyboardShortcut({
        onActivate: () => inspector.start(),
    });

    const machineConfigStringified = JSON.stringify(machineConfig);
    const currentMachineConfigStringified = JSON.stringify(currentMachineConfig);
    const currentMachineConfigId = currentMachineConfig?.id;

    useEffect(() => {
        if (machineConfig && machineConfigStringified !== currentMachineConfigStringified) {
            setCurrentMachineConfig(machineConfig);
        }
    }, [currentMachineConfigStringified, machineConfigStringified, machineConfig]);

    const memoizedMachine = useMemo(() => {
        return composeStateMachine({
            flowId: currentMachineConfigId as FlowId,
            getConfigByKey,
            router,
            queryClient,
            region,
            trackEvent,
            t,
        });
        // The router reference causes the machine to recreate itself at each router change.
        // router.push will continue working even if the reference is not updated though.
        // State machine guards will use window.location.pathname for the time being.
        // `t` is not as stable as it should be, causing unnecessary recreations. `getConfigByKey`
        // is memoized by useRegionalConfiguration and is stable.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        currentMachineConfigId,
        // getConfigByKey,
        queryClient,
        region,
        // router,
        trackEvent,
        // t,
    ]);

    const machine = useMachine(memoizedMachine, {
        systemId: currentMachineConfig?.id as FlowId,
        inspect: inspector.inspect,
    });

    return {
        machine,
        ready: !!currentMachineConfig,
    };
};

const useXstateClickEvent = () => {
    const { flowState, send } = useStateMachineContext();

    return useCallback(
        (xstateEvent: string = DTC_EVENTS.CLICK_SIGNIN) =>
            () => {
                if (flowState) {
                    send({
                        type: xstateEvent,
                    });
                }
            },
        [flowState, send],
    );
};

const useOnClickXstateEvent = (xstateEvent?: string) => {
    const getXstateClickCallback = useXstateClickEvent();
    return getXstateClickCallback(xstateEvent);
};

const useTnCTADefaultEnabled = (ffDefaultEnabled: keyof typeof FeatureFlags) => {
    const { getFeatureFlag } = useFeatureFlags();
    return getFeatureFlag(FeatureFlags[ffDefaultEnabled]);
};

const useCtaBehavior = ({
    destinationUrl,
    destinationLabel,
    xstateEvent,
    ffDefaultEnabled,
    isButton = false,
}: {
    destinationUrl: (typeof flowRoutes)[keyof typeof flowRoutes];
    destinationLabel: string;
    xstateEvent?: string;
    ffDefaultEnabled: keyof typeof FeatureFlags;
    isButton?: boolean;
}) => {
    const tnCTADefaultEnabled = useTnCTADefaultEnabled(ffDefaultEnabled);
    const onClickXstateEvent = useOnClickXstateEvent(xstateEvent);
    const { getRouteWithRegion } = useRegionRoute();
    // Flag off bypasses the region-aware flow router, so the destination must carry the region
    // itself (INTL legacy routes exist only prefixed). Empty means no link, the helper gives `/`.
    const regionalDestinationUrl = destinationUrl ? getRouteWithRegion(destinationUrl) : '';

    const handleNavigate = useCallback(() => {
        // Flag off means the destination is served by the legacy app, so a full
        // page load is required (mirrors the href branch used when !isButton).
        globalThis.location.assign(regionalDestinationUrl);
    }, [regionalDestinationUrl]);

    return useMemo(() => {
        const buttonBehavior = isButton
            ? { onClick: handleNavigate }
            : { href: regionalDestinationUrl };

        return {
            label: destinationLabel,
            'data-testid': `tn_nav_cta_${destinationLabel.replace(/\s+/g, '_').toLowerCase()}`,
            ...(tnCTADefaultEnabled ? { onClick: onClickXstateEvent } : buttonBehavior),
        };
    }, [
        destinationLabel,
        tnCTADefaultEnabled,
        onClickXstateEvent,
        regionalDestinationUrl,
        isButton,
        handleNavigate,
    ]);
};

const useLinkBehavior = ({
    xstateEvent,
    ffDefaultEnabled,
}: {
    xstateEvent?: string;
    ffDefaultEnabled: keyof typeof FeatureFlags;
}) => {
    const tnCTADefaultEnabled = useTnCTADefaultEnabled(ffDefaultEnabled);
    const onClickXstateEvent = useOnClickXstateEvent(xstateEvent);

    return useCallback(
        (destinationUrl: string, htmlTarget: string, filterOutputCb: (x: string) => boolean) => {
            return {
                ...(tnCTADefaultEnabled && filterOutputCb(destinationUrl)
                    ? { onClick: onClickXstateEvent, href: undefined, target: undefined }
                    : { onClick: undefined, href: destinationUrl, target: htmlTarget }),
            };
        },
        [tnCTADefaultEnabled, onClickXstateEvent],
    );
};

export { useCtaBehavior, useFlowMachine, useLinkBehavior, useXstateClickEvent };
